import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus } from '@phosphor-icons/react';

const EmployerJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [families, setFamilies] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', location: '', country: 'Canada',
    role_family_id: '', required_skills: '',
  });
  const [creating, setCreating] = useState(false);

  const loadJobs = () => api.get('/jobs/employer/mine').then((r) => setJobs(r.data));

  useEffect(() => {
    api.get('/role-families').then((r) => {
      setFamilies(r.data);
      if (r.data.length > 0) setForm((f) => ({ ...f, role_family_id: r.data[0].id }));
    });
    loadJobs();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/jobs', {
        title: form.title,
        description: form.description,
        location: form.location,
        country: form.country,
        role_family_id: form.role_family_id || null,
        required_skills: form.required_skills.split(',').map((s) => s.trim()).filter(Boolean),
        source: 'manual',
      });
      toast.success('Job posted');
      setForm({ ...form, title: '', description: '', location: '', required_skills: '' });
      loadJobs();
    } catch {
      toast.error('Failed to post');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">YOUR JOBS</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Post a role. See ranked candidates.</h1>

        <div className="grid lg:grid-cols-5 gap-6">
          <form onSubmit={submit} className="lg:col-span-2 border border-border bg-white p-6 space-y-4 h-fit" data-testid="new-job-form">
            <div className="label-eyebrow text-muted-foreground">POST A NEW JOB</div>
            <div>
              <Label className="label-eyebrow">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-none h-10" data-testid="job-title" />
            </div>
            <div>
              <Label className="label-eyebrow">Description</Label>
              <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none" rows={5} data-testid="job-desc" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-eyebrow">Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-none h-10" data-testid="job-location" />
              </div>
              <div>
                <Label className="label-eyebrow">Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger className="mt-1 rounded-none h-10" data-testid="job-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United States">USA</SelectItem>
                    <SelectItem value="Global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Role family</Label>
              <Select value={form.role_family_id} onValueChange={(v) => setForm({ ...form, role_family_id: v })}>
                <SelectTrigger className="mt-1 rounded-none h-10" data-testid="job-rf"><SelectValue placeholder="Select role family" /></SelectTrigger>
                <SelectContent>
                  {families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Required skills (comma-separated)</Label>
              <Input value={form.required_skills} onChange={(e) => setForm({ ...form, required_skills: e.target.value })} placeholder="communication, spreadsheets" className="mt-1 rounded-none h-10" data-testid="job-skills" />
            </div>
            <Button type="submit" disabled={creating} className="rounded-none w-full h-10" data-testid="post-job"><Plus size={14} className="mr-1" /> {creating ? 'Posting...' : 'Post job'}</Button>
          </form>

          <div className="lg:col-span-3 space-y-0 border border-border">
            <div className="p-4 border-b border-border bg-secondary">
              <div className="label-eyebrow text-muted-foreground">JOBS YOU POSTED</div>
            </div>
            {jobs.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">You haven't posted a job yet.</div>
            ) : (
              jobs.map((j) => (
                <Link to={`/employer/jobs/${j.id}`} key={j.id} className="block p-4 bg-white border-b border-border hover:bg-secondary/30" data-testid={`my-job-${j.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display font-black text-lg">{j.title}</div>
                      <div className="mono text-xs text-muted-foreground">{j.location || '—'} · {j.country}</div>
                    </div>
                    <div className="mono text-xs text-primary">VIEW →</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default EmployerJobs;
